import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import AuthHeader from '../components/AuthHeader';
import AuthFooter from '../components/AuthFooter';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter identifier, 2: Enter code, 3: New password
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    reset_code: '',
    new_password: '',
    confirm_password: ''
  });

  const handleRequestCode = async (e) => {
    e.preventDefault();
    
    if (!formData.identifier.trim()) {
      toast.error('Please enter your email or phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/password-reset/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: formData.identifier })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Reset code sent! Check your email/SMS');
        if (data.code_for_testing) {
          toast.info(`Test code: ${data.code_for_testing}`);
        }
        setStep(2);
      } else {
        toast.error(data.message || 'Failed to send reset code');
      }
    } catch (error) {
      console.error('Request code error:', error);
      toast.error('Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!formData.reset_code.trim()) {
      toast.error('Please enter the reset code');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/password-reset/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: formData.identifier,
          reset_code: formData.reset_code
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Code verified! Enter your new password');
        setStep(3);
      } else {
        toast.error(data.message || 'Invalid reset code');
      }
    } catch (error) {
      console.error('Verify code error:', error);
      toast.error('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/password-reset/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: formData.identifier,
          reset_code: formData.reset_code,
          new_password: formData.new_password
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Password reset successfully! Please login');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        toast.error(data.detail || data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      <AuthHeader />
      
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto">
              <img 
                src="/logo.png" 
                alt="AutomateSEO.ai" 
                className="h-16 w-auto mx-auto"
              />
            </div>
            <CardTitle className="text-3xl font-bold">
              {step === 1 && 'Forgot Password'}
              {step === 2 && 'Enter Reset Code'}
              {step === 3 && 'Reset Password'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Enter your email or phone number to receive a reset code'}
              {step === 2 && 'Enter the 6-digit code sent to your email/phone'}
              {step === 3 && 'Create your new password'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Step 1: Enter identifier */}
            {step === 1 && (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier">Email or Phone Number</Label>
                  <Input
                    id="identifier"
                    type="text"
                    placeholder="Enter your email or phone"
                    value={formData.identifier}
                    onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                    required
                    className="h-11"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : 'Send Reset Code'}
                </Button>
              </form>
            )}

            {/* Step 2: Enter code */}
            {step === 2 && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset_code">Reset Code</Label>
                  <Input
                    id="reset_code"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={formData.reset_code}
                    onChange={(e) => setFormData({ ...formData, reset_code: e.target.value })}
                    required
                    className="h-11 text-center text-2xl tracking-widest"
                    maxLength={6}
                  />
                  <p className="text-xs text-gray-500">
                    Code expires in 15 minutes
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full h-11" 
                    onClick={() => setStep(1)}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : 'Verify Code'}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 3: New password */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    placeholder="Enter new password"
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    required
                    className="h-11"
                    minLength={6}
                  />
                  <p className="text-xs text-gray-500">Must be at least 6 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    placeholder="Confirm new password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    required
                    className="h-11"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : 'Reset Password'}
                </Button>
              </form>
            )}

            {/* Back to login link */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Sign in
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <AuthFooter />
    </div>
  );
};

export default ForgotPassword;
